import { inject, Injectable, OnDestroy } from '@angular/core';
import { Client, State } from '@twilio/conversations';
import { Observable, ReplaySubject, Subject, Subscription } from 'rxjs';
import { subjectsComplete, unsubscribeAll } from 'src/app/common/app.helpers';
import { ChatEngineLifeСycleService } from '../../interfaces/chat-engine/chat-engine-lifecycle-service';
import { ChatEngineHandle, ChatTraceData } from '../../interfaces/chat-model.interface';
import { TwilioChatEngineHandle } from './twilio-chat-engine.handle';
import { TwilioClientAccessService } from './twilio-client-access-service';
import { TwilioErrorService, TwilioInternalOperations } from './twilio-error.service';

class TwilioEngineClientInternalHandle extends TwilioChatEngineHandle {
  public updateClient(maybeClient: Client | null): void {
    this.maybeClient = maybeClient;
  }
}

@Injectable({ providedIn: 'root' })
export class TwilioEngineLifecycleService
  extends ChatEngineLifeСycleService
  implements OnDestroy, TwilioClientAccessService
{
  public get engineReadinessChanged(): Observable<ChatEngineHandle> {
    return this.engineReadinessChanged$.asObservable();
  }

  public get connectionStatusChanged(): Observable<boolean> {
    return this.connectionStatusChanged$.asObservable();
  }

  public get expiredToken(): Observable<void> {
    return this.expiredToken$.asObservable();
  }

  private readonly engineHandle: TwilioEngineClientInternalHandle = new TwilioEngineClientInternalHandle(null);
  private connected = false;

  private engineReadinessChanged$: Subject<ChatEngineHandle> = new ReplaySubject(1);
  private connectionStatusChanged$: Subject<boolean> = new ReplaySubject(1);
  private expiredToken$: Subject<void> = new Subject();

  private subscriptions$: Subscription[] = [];

  private twilioErrorService = inject(TwilioErrorService);

  public constructor() {
    super();

    this.twilioErrorService.connectionStateRelatedErrorNoticed.subscribe(() => {
      if (this.connected) {
        this.connected = false;
        this.fireConnectionLost();
      }
    });
  }

  public ngOnDestroy(): void {
    unsubscribeAll(this.subscriptions$);
    subjectsComplete([this.engineReadinessChanged$, this.connectionStatusChanged$, this.expiredToken$]);
  }

  public getTwilioClient(): Client {
    return this.engineHandle.client;
  }

  public initEngine(token: string): void {
    this.engineHandle.updateClient(new Client(token));

    this.engineHandle.client.on('stateChanged', (state: State) => {
      if (state === 'initialized') {
        this.engineReadinessChanged$.next(this.engineHandle);

        this.connected = true;
        this.fireConnectionEstablished();

        this.subscribeToConnectionStateChanged();
        this.subscribeToTokenExpiredEvent();
      } else {
        this.twilioErrorService.onTwilioInternalOperationFailed(
          TwilioInternalOperations.CLIENT_INITIALIZATION,
          state,
          {} as ChatTraceData,
        );
      }
    });
  }

  public destroyEngine(): void {
    this.fireConnectionLost();
    this.engineReadinessChanged$.next(new TwilioChatEngineHandle(null));

    if (this.engineHandle.isValid()) {
      this.connected = false;
      this.engineHandle.client.removeAllListeners();
      this.engineHandle.client
        .shutdown()
        .catch((error: unknown) => {
          this.twilioErrorService.onTwilioInternalOperationFailed(
            TwilioInternalOperations.CLIENT_SHUTDOWN,
            error,
            {} as ChatTraceData,
          );
        })
        .finally(() => this.engineHandle.updateClient(null));
    }
  }

  private subscribeToConnectionStateChanged(): void {
    this.engineHandle.client.on('connectionStateChanged', () => {
      if (this.engineHandle.isValid()) {
        this.checkConnectionState();
      }
    });
  }

  private subscribeToTokenExpiredEvent(): void {
    this.engineHandle.client.on('tokenExpired', () => {
      console.warn('Twilio token expired');
      this.expiredToken$.next();
    });
  }

  private checkConnectionState(): void {
    if (this.engineHandle.client) {
      if (this.engineHandle.client.connectionState in ['connecting', 'disconnected', 'denied', 'error']) {
        if (this.connected) {
          this.connected = false;
          this.fireConnectionLost();
        }
      } else if (this.engineHandle.client.connectionState === 'connected') {
        if (!this.connected) {
          this.connected = true;
          this.fireConnectionEstablished();
        }
      }
    }
  }

  private fireConnectionLost(): void {
    this.connectionStatusChanged$.next(false);
  }

  private fireConnectionEstablished(): void {
    this.connectionStatusChanged$.next(true);
  }
}
